import { HttpStatusCode } from '@angular/common/http';
import {
	Component,
	EventEmitter,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FreudMessage, FreudMessageService } from '@freud-ds/web-components';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { IRecurrencyScheduleResponse } from 'app/interfaces/appointment.interface';
import {
	IBusySlot,
	RecurrencyRemoveRange,
} from 'app/interfaces/availability.interface';
import {
	INewEvent,
	IRecurrencyAppointment,
} from 'app/interfaces/event.interface';
import { ISidebar } from 'app/interfaces/sidebar.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { DateTimeService } from 'app/services/datetime.service';
import { EventsService } from 'app/services/events.service';
import { NetworkService } from 'app/services/network.service';
import { UserService } from 'app/services/user.service';
import { DateTime } from 'luxon';
import { IAppointmentFormValue } from 'modules/appointments/interfaces/appointment';
import { AppointmentSidebarService } from 'modules/appointments/services/appointment-sidebar.service';
import moment from 'moment';
import { MessageService } from 'primeng/api';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs/operators';
import { IAppointment } from 'shared/interfaces/appointment.interface';
import { UnleashService } from 'shared/unleash/unleash.service';

import { AvailabilitiesService } from '../../../../app/services/availabilities.service';

@Component({
	selector: 'appointment-sidebar',
	templateUrl: './appointment-sidebar.component.html',
	providers: [FreudMessageService],
	styleUrls: ['./appointment-sidebar.component.scss'],
})
export class AppointmentSidebarComponent implements OnInit, OnDestroy {
	@Output() emClosedSidebar = new EventEmitter();
	@Output() refreshEvents = new EventEmitter();
	public sidebar: ISidebar;
	public isOpen = false;
	public isLoading = false;
	public selectedTab = 'online';
	public _formValue: IAppointmentFormValue = null;
	public formInstance: FormGroup;
	public isUserOnline;
	public isUserPremium = false;
	public tabs = [];
	public action = 'create';
	public professionalId = null;
	public sidebarSubscription$ = null;
	public confirmEditBusySlotModal = false;
	public selectedOptionToEditBusySlot: RecurrencyRemoveRange;
	public isAppointmentReposition = false;
	public invalidAppointmentNoShowSelected = false;
	public professionalMainType: string;
	public conflictInviteNewAppointmentError: {
		showModal: boolean;
		appointments: IAppointment[];
		busySlots: IBusySlot[];
		appointmentToCreate: INewEvent | null;
	} = {
		showModal: false,
		appointments: [],
		busySlots: [],
		appointmentToCreate: null,
	};
	constructor(
		protected network: NetworkService,
		protected notifications: ZkNotificationService,
		private translate: TranslateService,
		readonly messageService: MessageService,
		readonly events: EventsService,
		private dateTime: DateTimeService,
		private userService: UserService,
		private availabilities: AvailabilitiesService,
		public sidebarService: AppointmentSidebarService,
		public actionTracker: ActionTrackerService,
		private unleashService: UnleashService
	) {}

	ngOnInit(): void {
		this.userService.getUserId().then((res) => {
			this.professionalId = res;
		});
		this.isUserOnline = this.network.checkConnectivity();
		this.userService.checkSubscriptionAndContactTiers().then((user) => {
			this.isUserPremium = user.hasActiveSubscription;
			this.setTabs();
		});
		this.sidebarSubscription$ = this.sidebarService.onStateChanges
			.pipe(skip(1), debounceTime(500), distinctUntilChanged())
			.subscribe((value) => {
				const { sidebar, isOpen } = value;
				this.userService.getProfileData().then((user) => {
					this.professionalMainType = user.mainType;
					this.trackAppointmentSidebarInit({
						from: sidebar?.from,
						specialist_type: user.mainType,
					});
				});
				if (!sidebar) {
					return;
				}
				this.sidebar = { ...sidebar };
				if (this.sidebar?.id) {
					this.action = 'edit';
					if (this.sidebar.address) {
						this.selectedTab = 'presencial';
					}
					if (sidebar.isBusySlot) {
						this.selectedTab = 'particular';
					}
				} else {
					this.action = 'create';
					this.selectedTab = 'online';
				}
				if (this.sidebar.isReposition) this.isAppointmentReposition = true;
				this.setFormSettings();
				this.setTabs();
				this.isOpen = isOpen;
			});
	}

	ngOnDestroy(): void {
		this.sidebarService.setSidebar(false, null);
		this.sidebarSubscription$.unsubscribe();
	}

	async setFormSettings() {
		this.formValue = {
			contact: this.sidebar.contactId,
			dates: [
				{
					date: this.sidebar.date,
					time: this.sidebar.time,
				},
			],
			professionalAddress: this.sidebar.address,
			price: 0,
		};
		if (this.sidebar.isBusySlot) {
			this.formValue.observations = this.sidebar.description;
			this.formValue.frequencySelect = this.sidebar.recurrencyType;
			this.formValue.dates = [
				{
					date: this.sidebar.startDate,
					time: this.sidebar.startDate,
				},
				{
					date: this.sidebar.endDate,
					time: this.sidebar.endDate,
				},
			];
		}
	}

	setTabs() {
		this.tabs = this.getTabs();
	}

	getTabs() {
		const tabs = [
			{
				label: 'Sessão online',
				value: 'online',
				disabled:
					(this.sidebar?.id && !!this.sidebar.address) ||
					this.sidebar?.isBusySlot,
			},
			{
				label: 'Sessão presencial',
				value: 'presencial',
				icon: 'diamond',
				disabled:
					!this.isUserPremium ||
					(this.sidebar?.id && !this.sidebar.address) ||
					this.sidebar?.isBusySlot,
			},
			{ label: 'Compromisso particular', value: 'particular' },
		]
			.filter(
				this.sidebar?.contactId
					? (tab) => tab.value !== 'particular'
					: (tab) => tab
			)
			.filter(
				!this.unleashService.isEnabled('onsite-sessions')
					? (tab) => tab.value !== 'presencial'
					: (tab) => tab
			);

		return tabs;
	}

	set formValue(value) {
		this._formValue = value;
	}

	get formValue() {
		return this._formValue;
	}

	setFormInstance(form) {
		this.formInstance = form;
	}

	changeTab(tab) {
		this.selectedTab = tab;
		if (this.selectedTab !== tab) {
			this.actionTracker.eventTracker(
				SCOPE_TYPE.Appointment,
				SUBSCOPE_TYPE.Sidebar,
				ITEM_TYPE.Tab,
				INTERACTION_TYPE.Click,
				{ tab: this.selectedTab }
			);
			this.formInstance.reset();
		}
	}

	cancel(clicked = false) {
		this.formInstance.reset();
		this.sidebarService.setSidebar(false, null);
		this.isOpen = false;
		if (clicked) this.trackAppointmentSidebarCancel();
	}

	public async schedule() {
		if (
			this.selectedTab === 'particular' &&
			!this.selectedOptionToEditBusySlot &&
			this.sidebar.recurrencyId &&
			this.action === 'edit'
		) {
			this.toggleConfirmEditBusySlotModal();
			return;
		}

		this.isLoading = true;
		if (!this.isUserOnline) {
			this.setError({
				messageI18n: 'APPOINTMENTS.NOCONNECTION',
			});
			return;
		}
		if (
			!this.formInstance.value.contactExistsAsCustomer &&
			this.formInstance.value.dates.length > 1
		) {
			this.setError({
				messageI18n:
					'APPOINTMENTS.CUSTOMER_DOESNT_YET_HAVE_CONFIRMED_REGISTRATION',
			});
			return;
		}

		if (this.selectedTab === 'particular' && !this.isInitialTimeValid()) {
			this.setError({
				messageI18n: 'APPOINTMENTS.INITIAL_TIME_BIGGER_THAN_FINAL',
			});
			return;
		}

		if (this.isFormValid()) {
			if (this.selectedTab === 'particular') {
				this.action === 'edit'
					? this.updateBusySlot()
					: this.registerParticularAppointment();
				return;
			}

			this.action === 'edit'
				? this.updateAppointment()
				: this.registerAppointment();
		} else {
			this.isLoading = false;
		}
	}

	isInitialTimeValid() {
		const initialTime = DateTime.fromJSDate(
			this.formInstance.value.particularDate.initialTime
		);
		const finalTime = DateTime.fromJSDate(
			this.formInstance.value.particularDate.finalTime
		);

		if (initialTime.hour > finalTime.hour) {
			return false;
		}

		if (
			initialTime.hour === finalTime.hour &&
			initialTime.minute > finalTime.minute
		) {
			return false;
		}

		return true;
	}

	isFormValid() {
		this.invalidAppointmentNoShowSelected =
			!this.formInstance.controls.repositionOriginalEventId.valid;
		if (!this.formInstance.valid) {
			this.setError({
				messageI18n: 'APPOINTMENTS.INVALID_FORM',
			});
			this.formInstance.markAllAsTouched();
		}
		return this.formInstance.valid;
	}

	public async registerAppointment() {
		this.actionTracker.eventTracker(
			SCOPE_TYPE.Appointment,
			SUBSCOPE_TYPE.Details,
			ITEM_TYPE.BtnSchedule,
			INTERACTION_TYPE.Click,
			{
				selected: this.selectedTab,
				expiration: this.formInstance.value.expiration / 60,
				multiple:
					this.formInstance.value.dates.length > 1 ||
					this.formInstance.value.recurrency !== 'no-repeat',
				quantity: this.getSessionsQuantity(),
				recurrency: this.formInstance.value.recurrency,
			}
		);
		this.handleDateFormat(this.formInstance.value.dates[0]);
		const isSingleAppointment =
			this.formInstance.get('recurrency').value === 'no-repeat';

		isSingleAppointment
			? this.registerSingleAppointment()
			: this.registerRecurrencyAppointments();
	}

	public async updateAppointment() {
		const appointment = {
			userId: await this.userService.getUserId(),
			timeZone: this.dateTime.getTimezoneName(),
			startDate: this.handleDateFormat(this.formInstance.value.dates[0]),
			professionalAddressId:
				this.formInstance.value.professionalAddress?.id ?? null,
		};
		this.events
			.updateEvent(this.sidebar?.id, appointment)
			.toPromise()
			.then(() => {
				const message: FreudMessage = {
					severity: 'success',
					summary: this.translate.instant(
						'APPOINTMENTS.RESCHEDULE_SUCCESSFULLY'
					),
					detail: null,
				};
				this.messageService.add(message);
				this.onAppointmentSaved(
					DateTime.fromISO(appointment.startDate).toJSDate()
				);
			})
			.catch((e) => {
				const { error } = e;
				this.setError(error);
			});
	}

	async registerParticularAppointment() {
		const appointment: IBusySlot = {
			professionalId: await this.userService.getUserId(),
			startDate: this.handleDateForParticularAppointment(
				this.formInstance.value.particularDate.date,
				this.formInstance.value.particularDate.initialTime
			),
			endDate: this.handleDateForParticularAppointment(
				this.formInstance.value.particularDate.date,
				this.formInstance.value.particularDate.finalTime
			),
			description: this.formInstance.value.observations,
			recurrencyType: this.formInstance.value.frequencySelect,
		};
		this.availabilities.addBusySlot(appointment).subscribe(
			() => {
				const message: FreudMessage = {
					severity: 'success',
					summary: this.translate.instant(
						'APPOINTMENTS.PARTICULAR_APPOINTMENT_SAVED'
					),
					detail: null,
				};
				this.messageService.add(message);
				this.trackAppointmentSubmit(INTERACTION_TYPE.Success, {
					multiple: false,
				});
				this.onAppointmentSaved(appointment.startDate);
			},
			(e) => {
				const { error } = e;
				this.trackAppointmentSubmit(INTERACTION_TYPE.Error, {
					multiple: false,
				});
				this.setError(error);
			}
		);
	}

	registerRecurrencyAppointments() {
		const appointment: IRecurrencyAppointment = {
			inviteExpiresMinutesBeforeSessions: this.formInstance.value.expiration,
			price: this.formInstance.value.price,
			sessions: this.formInstance.value.dates.map((date) =>
				this.handleDateFormat(date)
			),
			recurrencyType: this.formInstance.value.recurrency,
			totalSessions: this.formInstance.value.recurrencyMaxSessions,
			userId: this.formInstance.value.userId,
			contactId: this.formInstance.value.contact,
			professionalAddressId:
				this.selectedTab === 'presencial'
					? this.formInstance.value.professionalAddress.id
					: null,
		};

		this.events.scheduleRecurrencyAppointment(appointment).subscribe(
			(result: IRecurrencyScheduleResponse) => {
				const errorMessage =
					result.data?.failed > 0
						? this.translate.instant(
								'APPOINTMENTS.SCHEDULE_RESPONSE.RESPONSE_DESCRIPTION',
								{ number: result.data?.failed }
						  )
						: null;
				const message: FreudMessage = {
					severity: 'success',
					summary: this.translate.instant(
						'APPOINTMENTS.SCHEDULE_RESPONSE.RESPONSE_TITLE',
						{
							number:
								result.data?.failed > 0
									? result.data?.success
									: this.getSessionsQuantity(),
						}
					),
					detail: errorMessage,
					life: 3500,
				};
				this.messageService.add(message);
				this.trackAppointmentSubmit(INTERACTION_TYPE.Success, {
					multiple: true,
				});
				this.onAppointmentSaved(new Date());
			},
			(e) => {
				this.trackAppointmentSubmit(INTERACTION_TYPE.Error, { multiple: true });
				const { error } = e;
				if (error.data) {
					this.setMessageForRecurrency(error);
					this.isLoading = false;
					if (error.data.success > 0) {
						this.onAppointmentSaved(new Date());
					}
					return;
				}
				this.setError(error);
			}
		);
	}

	setMessageForRecurrency(error: {
		data: { failed: number; success: number };
	}) {
		const errorMessage =
			error.data.failed > 0
				? this.translate.instant(
						'APPOINTMENTS.SCHEDULE_RESPONSE.RESPONSE_DESCRIPTION',
						{ number: error.data.failed }
				  )
				: null;
		const message: FreudMessage = {
			severity: error.data.success > 0 ? 'success' : 'error',
			summary:
				error.data.success > 0
					? this.translate.instant(
							'APPOINTMENTS.SCHEDULE_RESPONSE.RESPONSE_TITLE',
							{ number: error.data.success }
					  )
					: this.translate.instant(
							'APPOINTMENTS.SCHEDULE_RESPONSE.RESPONSE_TITLE_FAILED'
					  ),
			detail: errorMessage,
			life: 3500,
		};
		this.messageService.add(message);
	}

	async registerSingleAppointment() {
		const appointment: INewEvent = {
			inviteExpiresMinutesBeforeSessions: this.formInstance.value.expiration,
			professionalId: await this.userService.getUserId(),
			professionalOnline: false,
			price: this.formInstance.value.price,
			startDate: this.handleDateFormat(this.formInstance.value.dates[0]),
			timezone: this.dateTime.getTimezoneName(),
			type: 'invite',
			userId: this.formInstance.value.userId,
			contactId: this.formInstance.value.contact,
			contactName: null,
			professionalAddressId:
				this.selectedTab === 'presencial'
					? this.formInstance.value.professionalAddress.id
					: null,
		};

		if (this.formInstance.value?.isReposition) {
			appointment.repositionOriginalEventId =
				this.formInstance.value?.repositionOriginalEventId ?? null;
		}

		this.conflictInviteNewAppointmentError.appointmentToCreate = appointment;

		this.events.saveNewEvent(appointment).subscribe(
			(result) => {
				const message: FreudMessage = {
					severity: 'success',
					summary: this.translate.instant('MESSAGES.NEWEVENT.INVITEWASSENT'),
					detail: null,
				};
				this.messageService.add(message);
				this.events.updateLocalEvent(result);
				this.trackAppointmentSubmit(INTERACTION_TYPE.Success, {
					multiple: false,
				});
				if (this.formInstance.value?.isReposition) {
					this.trackAppointmentRepositionSubmit(
						INTERACTION_TYPE.Success,
						this.formInstance.value
					);
				}
				this.onAppointmentSaved(result.day);
			},
			(e) => {
				const { error } = e;
				this.trackAppointmentSubmit(INTERACTION_TYPE.Error, {
					multiple: false,
				});

				if (
					e?.status === HttpStatusCode.Conflict &&
					error?.data?.errorCode === 'ScheduleConflictError'
				) {
					this.conflictInviteNewAppointmentError.appointments =
						error.data?.appointments || [];
					this.conflictInviteNewAppointmentError.busySlots =
						error.data?.busySlots || [];

					if (
						this.conflictInviteNewAppointmentError.appointments.length === 0 &&
						this.conflictInviteNewAppointmentError.busySlots.length > 0
					) {
						this.conflictInviteNewAppointmentError.showModal = true;
						this.isLoading = false;
						return;
					}
				}
				this.setError(error);
			}
		);
	}

	trackAppointmentSubmit(
		result: INTERACTION_TYPE.Success | INTERACTION_TYPE.Error,
		{ multiple }
	) {
		this.actionTracker.eventTracker(
			SCOPE_TYPE.Appointment,
			SUBSCOPE_TYPE.Sidebar,
			ITEM_TYPE.Submit,
			result,
			{
				type: this.selectedTab,
				professionalId: this.professionalId,
				action: this.action,
				multiple,
				specialist_type: this.professionalMainType,
				from: this.sidebar?.from,
				addressId:
					this.selectedTab === 'presencial'
						? this.formInstance.value.professionalAddress.id
						: null,
				quantity: this.getSessionsQuantity(),
				recurrency: this.formInstance.value.recurrency,
			}
		);
	}

	getSessionsQuantity() {
		let sessionsQuantity =
			this.formInstance.value.recurrency === 'custom'
				? this.formInstance.value.dates.length
				: this.formInstance.value.recurrencyMaxSessions;

		sessionsQuantity =
			this.formInstance.value.recurrency === 'no-repeat' ? 1 : sessionsQuantity;
		return sessionsQuantity;
	}

	trackAppointmentRepositionSubmit(
		result: INTERACTION_TYPE.Success | INTERACTION_TYPE.Error,
		payload: any
	) {
		this.actionTracker.eventTracker(
			SCOPE_TYPE.AppointmentReposition,
			SUBSCOPE_TYPE.Sidebar,
			ITEM_TYPE.Submit,
			result,
			payload
		);
	}

	trackAppointmentSidebarInit(payload: any) {
		this.actionTracker.eventTracker(
			SCOPE_TYPE.Appointment,
			SUBSCOPE_TYPE.Sidebar,
			ITEM_TYPE.Create,
			INTERACTION_TYPE.Click,
			payload
		);
	}

	trackAppointmentSidebarCancel() {
		this.actionTracker.eventTracker(
			SCOPE_TYPE.Appointment,
			SUBSCOPE_TYPE.Sidebar,
			ITEM_TYPE.Cancel,
			INTERACTION_TYPE.Click,
			{
				tab: this.selectedTab,
				from: this.sidebar?.from,
				specialist_type: this.professionalMainType,
			}
		);
	}

	async onAppointmentSaved(date?: Date) {
		this.isLoading = false;
		if (date) {
			const minDate = moment.utc(date).startOf('day');
			const maxDate = moment.utc(date).endOf('day');
			await this.events.updateLocalEvents({ minDate, maxDate });
		}

		this.refreshEvents.emit(true);
		this.selectedTab = 'online';
		this.cancel();
	}

	handleDateFormat({ date, time }) {
		const lxDate = DateTime.fromJSDate(date);
		const lxHour = DateTime.fromJSDate(time).get('hour');
		const lxMinute = DateTime.fromJSDate(time).get('minute');
		return lxDate.set({ hour: lxHour, minute: lxMinute }).toISO();
	}

	handleDateForParticularAppointment(date, time) {
		const lxDay = DateTime.fromJSDate(date);
		const lxTime = DateTime.fromJSDate(time);
		return lxDay
			.set({
				hour: lxTime.get('hour'),
				minute: lxTime.get('minute'),
			})
			.toJSDate();
	}

	setError(error?) {
		const defaultError =
			this.getSessionsQuantity() > 1
				? 'APPOINTMENTS.DEFAULT_ERROR_MULTIPLE_SCHEDULE'
				: 'APPOINTMENTS.DEFAULT_ERROR_SINGLE_SCHEDULE';
		const message: FreudMessage = {
			severity: 'error',
			summary: this.translate.instant(
				error.messageI18n ? error.messageI18n : defaultError
			),
			detail: null,
		};
		this.messageService.add(message);
		this.isLoading = false;
		return;
	}

	toggleConfirmEditBusySlotModal() {
		this.confirmEditBusySlotModal = !this.confirmEditBusySlotModal;
	}

	async updateBusySlot() {
		const data = {
			id: this.sidebar.id,
			recurrencyId: this.sidebar.recurrencyId,
			recurrencyEditRange:
				this.selectedOptionToEditBusySlot || RecurrencyRemoveRange.ONLY,
			description: this.formInstance.value.observations,
			startDate: this.handleDateForParticularAppointment(
				this.formInstance.value.particularDate.date,
				this.formInstance.value.particularDate.initialTime
			),
			endDate: this.handleDateForParticularAppointment(
				this.formInstance.value.particularDate.date,
				this.formInstance.value.particularDate.finalTime
			),
		};
		this.availabilities.updateBusySlot(data).subscribe(
			() => {
				const message: FreudMessage = {
					severity: 'success',
					summary: this.translate.instant(
						'APPOINTMENTS.PARTICULAR_APPOINTMENT_SAVED'
					),
					detail: null,
				};
				this.messageService.add(message);
				this.trackAppointmentSubmit(INTERACTION_TYPE.Success, {
					multiple: false,
				});
				this.onAppointmentSaved(data.startDate);
			},
			(e) => {
				const { error } = e;
				this.trackAppointmentSubmit(INTERACTION_TYPE.Error, {
					multiple: false,
				});
				this.setError(error);
			}
		);
	}

	setOptionToEditBusySlot(value: RecurrencyRemoveRange) {
		this.selectedOptionToEditBusySlot = value;
	}

	confirmEditBusySlot(value: RecurrencyRemoveRange) {
		this.setOptionToEditBusySlot(value);
		this.toggleConfirmEditBusySlotModal();
		this.schedule();
	}

	confirmConflictInviteNewAppointment(appointment: INewEvent) {
		this.isLoading = true;
		this.events.saveNewEvent(appointment).subscribe(
			(result) => {
				const message: FreudMessage = {
					severity: 'success',
					summary: this.translate.instant('MESSAGES.NEWEVENT.INVITEWASSENT'),
					detail: null,
				};
				this.messageService.add(message);
				this.events.updateLocalEvent(result);
				this.trackAppointmentSubmit(INTERACTION_TYPE.Success, {
					multiple: false,
				});
				if (this.formInstance.value?.isReposition) {
					this.trackAppointmentRepositionSubmit(
						INTERACTION_TYPE.Success,
						this.formInstance.value
					);
				}
				this.onAppointmentSaved(result.day);
			},
			(e) => {
				const { error } = e;
				this.trackAppointmentSubmit(INTERACTION_TYPE.Error, {
					multiple: false,
				});

				this.setError(error);
			}
		);
	}

	closeConflictInviteNewAppointmentModal() {
		this.conflictInviteNewAppointmentError.showModal = false;
	}
}
